import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from "react-router-dom";
import Grid from '@mui/material/Grid2';

import { 
  Campaign,
  Character,
  CharacterSheetInterface,
  TransformInstance,
} from "@dndModels";
import { User } from "@models";

import API from "@dndUtilities/system_api";
import SmartAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import EditableLabel from "@components/input/strings/EditableLabel";

import CharacterMainDetails from "@dndComponents/character/CharacterMainDetails";
import LoadingBox from "@components/input/LoadingBox";

import CharacterBackgroundButton from "@dndComponents/character/background/CharacterBackgroundButton";
import CharacterRaceButton from "@dndComponents/character/race/CharacterRaceButton";
import CharacterClassButton from "@dndComponents/character/classes/CharacterClassButton";
// import CharacterHP from "./misc/CharacterHP";
import CharacterRestButton from "@dndComponents/character/misc/CharacterRestButton";
import CharacterInspirationButton from "@dndComponents/character/misc/CharacterInspirationButton";
import CharacterGiftButton from "@dndComponents/character/gifts/CharacterGiftButton";
import CharacterImageButton from "@dndComponents/character/misc/CharacterImageButton";
import CharacterSpeedButton from "@dndComponents/character/misc/CharacterSpeedButton";
import CharacterACButton from "@dndComponents/character/misc/CharacterACButton";
import CharacterTransformButton from "@dndComponents/character/transform/CharacterTransformButton";
import CharacterSizeButton from "@dndComponents/character/misc/CharacterSizeButton";
import CharacterKnowledgeButton from "@dndComponents/character/knowledge/CharacterKnowledgeButton";
import CustomRolls from "@dndComponents/character/CustomRolls";
import CharacterInitiative from "@dndComponents/character/misc/CharacterInitiative";
import HPInput from "@dndComponents/other/HPInput";
import CharacterAdvantageButton from "@dndComponents/character/misc/CharacterAdvantageButton";
import CharacterColorsButton from "@dndComponents/character/colors/CharacterColorsButton";
import CharacterBastionButton from "@dndComponents/character/bastions/CharacterBastionButton";
import CharacterMailButton from "@dndComponents/character/receivables/CharacterMailButton";
import CharacterLevelDisplay from "@dndComponents/character/misc/CharacterLevelDisplay";
import CharacterProficiencyBonus from "@dndComponents/character/misc/CharacterProficiencyBonus";

interface Props { 
  character: Character;
  shared?: boolean;
  updateCharacter: (obj: Character, pieces?: string[]) => void;
}

const api = API.getInstance();
const smart_api = SmartAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSheet({ 
  character,
  updateCharacter,
  shared = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [use_transform, setUseTransform] = useState(character.transform !== null ? true : false);
  const [loading_detail, setLoadingDetail] = useState("");
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [char_interface, setCharInterface] = useState<CharacterSheetInterface>(character.transform === null ? character : character.transform);
  const [owner, setOwner] = useState<User | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculate = useCallback(() => {
    setTimeout(() => {
      console.log("recalculate");
      char_util.repopulateCharFeatureBases(character);
    }, 1000);
  }, [character]);

  const spellsChanged = useCallback( async () => {
    setTimeout( async () => {
      console.log("spellsChanged");
      await char_util.doRecalcs2(character, character);
      char_util.recalcSpells(character);
    }, 1000);
  }, [character]);

  const transformed = useCallback(() => {
    console.log(character.transform);
    if (character.transform && character.use_transform) {
      setCharInterface(character.transform);
      setUseTransform(true);
    } else {
      setCharInterface(character);
      setUseTransform(false);
    }
  }, [character]);
  
  const load = useCallback(() => {
    const new_obj = character;
    if (new_obj.campaign_id === "" || !api.online) {
      setCharInterface(new_obj.transform === null ? new_obj : new_obj.transform);
      if (api.user && new_obj.owner_id === api.user._id) {
        setOwner(api.user);
      } else {
        setLoading(true);
        const filter = {
          "_id*ObjectIDs": [new_obj.owner_id]
        };
        
        if (smart_api.apis.user) {
          smart_api.apis.user.getObjects({
            data_type: "user", 
            filter,
            ignore_templates: false
          }).then(res => {
            if (res.length === 1 && res[0] instanceof User) {
              setOwner(res[0]);
            }
            setLoading(false);
          });
        }
      }
    } else {
      setLoading(true);
      setLoadingDetail("Campaign");
      // debugger;
      api.getObjects({ data_type: "campaign", filter: { "_id*ObjectIDs": [new_obj.campaign_id] } }).then(res2 => {
        if (res2.length === 1 && res2[0] instanceof Campaign) {
          const new_campaign = res2[0];
          api.getObjects({data_type: "character", filter: { campaign_id: new_campaign._id }}).then(res3 => {
            new_campaign.characters = res3 as Character[];
            new_obj.campaign = new_campaign;
            setCharInterface(new_obj.transform === null ? new_obj : new_obj.transform);
            setCampaign(new_campaign);
            if (api.user && new_obj.owner_id === api.user._id) {
              setOwner(api.user);
              setLoading(false);
            } else {
              setLoading(false);
              const filter = {
                "_id*ObjectIDs": [new_obj.owner_id]
              };
        
              if (smart_api.apis.user) {
                smart_api.apis.user.getObjects({
                  data_type: "user", 
                  filter,
                  ignore_templates: false
                }).then(res => {
                  if (res.length === 1 && res[0] instanceof User) {
                    setOwner(res[0]);
                  }
                  setLoading(false);
                });
              }
            }
          });
        }
      });
    }
  }, [character]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Recalculate", mounted, recalculate);
      // character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      character.subscription_holder.subscribe("Transform", mounted, transformed);
      character.subscription_holder.subscribe("Spells", mounted, spellsChanged);
      // character.subscription_holder.subscribe("Items", mounted, recalculate);
      load();
    }
  }, [fetched, character, load, recalculate, spellsChanged, transformed]);

  const renderBar1 = () => {
    // console.log("renderBar1");
    const recalc_time = character.recalc_time;
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        className="bg_black_transp"
        size={{
          xs: 12
        }}>
        <Grid
          size={{
            xs: 4
          }}>
          <div style={{ float: "left" }}>
            <CharacterImageButton 
              character={character}
              disabled={shared}
              onChange={(changed: Character) => {
                changed.changed();
                changed.subscription_holder.anyChanged();
                updateCharacter(changed, ["main"]);
              }}
            />
          </div>
          <div style={{ float: "left" }}>
            <Grid container spacing={0} direction="row">
              <Grid
                style={{ marginLeft: "4px" }}
                size={{
                  xs: 12
                }}>
                <EditableLabel 
                  name="Name"
                  value={character.name}
                  fontSize={24}
                  disabled={shared}
                  flash_on_empty
                  onChange={(changed: string) => {
                    const new_obj = character;
                    new_obj.name = changed;
                    new_obj.changed();
                    new_obj.subscription_holder.anyChanged();
                    updateCharacter(new_obj, ["main"]);
                  }}
                />
              </Grid>
              <Grid
                style={{ marginLeft: "4px" }}
                size={{
                  xs: 12
                }}>
                <CharacterLevelDisplay character={character} />
              </Grid>
              { owner && 
                <Grid
                  style={{ 
                      fontSize: "8px", 
                      color: "gray", 
                      marginLeft: "4px"
                    }}
                  size={{
                    xs: 12
                  }}>
                  { owner.username }
                </Grid>
              }
              <Grid
                style={{ marginLeft: "4px" }}
                size={{
                  xs: 12
                }}>
                <CharacterTransformButton
                  character={character}
                  use_transform={use_transform}
                  show_name
                  onChange={() => {
                    if (character.transform === null && char_interface instanceof TransformInstance) {
                      setCharInterface(character);
                      setUseTransform(false);
                    }
                    updateCharacter(character, ["features"]);
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 5
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            <CharacterClassButton 
              character={character}
              disabled={shared}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <CharacterRaceButton
              character={character}
              disabled={shared}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            { !character.sidekick &&
              <CharacterBackgroundButton
                character={character}
                disabled={shared}
              />
            }
          </Grid>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 3
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            <div style={{ float: "right", marginRight: "4px", maxWidth: "245px" }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <HPInput 
                    char={char_interface}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CharacterInspirationButton 
                    char={character}
                    char_interface={char_interface}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CharacterAdvantageButton 
                    character={character}
                    char_interface={char_interface}
                  />
                </Grid>
              </Grid>
            </div>
            <div style={{ float: "right", marginRight: "4px", maxWidth: "125px" }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CharacterRestButton 
                    character={character}
                    char_interface={char_interface}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CharacterKnowledgeButton 
                    char={character}
                    onChange={() => {
                      updateCharacter(character, ["main"]);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CharacterGiftButton 
                    character={character}
                    disabled={shared}
                    recalc_time={recalc_time}
                    onChange={(changed: Character) => {
                      const new_obj = changed;
                      char_util.repopulateCharFeatureBases(new_obj).then(() => {
                        updateCharacter(character, ["main"]);
                      });
                    }}
                  />
                </Grid>
                { character.character_options.use_bastions && 
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <CharacterBastionButton 
                      character={character}
                      disabled={shared}
                      recalc_time={recalc_time}
                    />
                  </Grid>
                }
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CharacterTransformButton
                    character={character}
                    use_transform={use_transform}
                    onChange={() => {
                      if (character.transform === null && char_interface instanceof TransformInstance) {
                        setCharInterface(character);
                        setUseTransform(false);
                      }
                      updateCharacter(character, ["features"]);
                    }}
                  />
                </Grid>
                { character.campaign &&
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <CharacterMailButton 
                      char={character}
                      disabled={shared}
                      recalc_time={recalc_time}
                      onChange={(changed: Character) => {
                        const new_obj = changed;
                        char_util.repopulateCharFeatureBases(new_obj).then(() => {
                          updateCharacter(character, ["main"]);
                        });
                      }}
                    />
                  </Grid>
                }
                { !shared &&
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <CharacterColorsButton 
                      character={character}
                    />
                  </Grid>
                }
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const renderBar2 = () => {
    // console.log("renderBar2");
    return (
      <>
        <Grid
          style={{ borderTop: "1px solid" }}
          className="bg_black_transp"
          size={{
            xs: 3
          }}>
          <CharacterSizeButton
            character={character}
            char_interface={char_interface}
          />
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          className="bg_black_transp"
          size={{
            xs: 9
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 3
              }}>
              <CharacterProficiencyBonus
                character={character}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CharacterSpeedButton
                character={character}
                char_interface={char_interface}
              />
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 3
              }}>
              <CharacterInitiative
                character={character}
                char_interface={char_interface}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CharacterACButton
                char={character}
                char_int={char_interface}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox message={loading_detail} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />);
  } else if (api.user === null && !shared) {
    return <Navigate to="/labs" />;
  } else { 
    return (
      <Grid container spacing={0} direction="row" className="dracoLich" style={{ 
        lineHeight: "1.5", 
        backgroundSize: `${window.innerWidth}px`, 
        backgroundAttachment: "fixed", 
        backgroundImage: (character.bg_image_url === "" ? undefined : `url(${character.bg_image_url})`),
      }}>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { renderBar1() }
          { renderBar2() }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterMainDetails 
            character={character}
            char_interface={char_interface}
            campaign={campaign}
            shared={shared}
          />
        </Grid>
        <CustomRolls
          char_interface={char_interface}
        />
      </Grid>
    ); 
  }
}
